package com.techspiration.webuserapp.home

import androidx.compose.runtime.Composable
import com.techspiration.webuserapp.site.FeatureCard
import com.techspiration.webuserapp.site.SectionContainer
import com.techspiration.webuserapp.site.StoreBadges
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/**
 * Herkese açık tanıtım sitesinin ana (landing) içeriği: hero, ilan sayıları + örnek ilanlar,
 * özellikler, nasıl-çalışır, ekran görüntüleri, SSS ve indirme çağrısı bölümlerini sırasıyla dizer.
 *
 * Bölümler `site` paketindeki ortak bileşenlerle ([SectionContainer], [FeatureCard],
 * [StoreBadges]) ve `index.html`'de tanımlı tasarım sistemi CSS class'larıyla kurulur —
 * burada yeni class eklenmez, yalnız zorunlu noktalarda küçük inline stil kullanılır.
 */
@Composable
fun HomeScreen() {
    HeroSection()
    // Hero'daki başlık + açıklamanın hemen altında: canlı sayaçlar, onların hemen altında
    // temsilî ilan kartları. İkisi TEK bölümde — aralarına başka içerik girmemeli.
    ListingStatsSection()
    FeaturesSection()
    HowItWorksSection()
    ScreenshotsSection()
    FaqSection()
    DownloadCtaSection()
}

/**
 * Hero — sol kolonda başlık/açıklama/indirme rozetleri, sağ kolonda ana ekran görüntüsü.
 * `SectionContainer` yerine `.hero` arka plan gradyanını taşıyan kendi `container`/`hero-grid`
 * yapısını kurar.
 */
@Composable
private fun HeroSection() {
    Div(attrs = { classes("hero") }) {
        Div(attrs = { classes("container") }) {
            Div(attrs = { classes("hero-grid") }) {
                Div(
                    attrs = {
                        // Reset ile tüm elemanların margin'i 0 — dikey ritim için minik inline gap.
                        attr("style", "display:flex;flex-direction:column;gap:var(--s6)")
                    },
                ) {
                    Img(
                        src = "/brand-logo.png",
                        alt = "Atama Bildirim uygulama logosu",
                        attrs = { classes("brand-logo") },
                    )
                    H1(attrs = { classes("t-display") }) {
                        Text("Kamu ilanlarını kaçırma, sana uygun olanı anında öğren")
                    }
                    P(attrs = { classes("t-body") }) {
                        Text(
                            "Atama Bildirim, Türkiye'deki kamu iş ilanlarını tek yerde toplar; " +
                                "profiline uygun bir ilan açıldığında seni anında haberdar eder ve " +
                                "saniyeler içinde başvurmanı sağlar.",
                        )
                    }
                    StoreBadges()
                    P(attrs = { classes("t-label") }) {
                        Text("Ücretsiz indir · Anlık bildirim · Reklamsız premium seçeneği")
                    }
                }
                Img(
                    src = "/screenshots/screenshot_01.png",
                    alt = "Atama Bildirim ana ekran",
                    attrs = { classes("shot") },
                )
            }
        }
    }
}

/** Özellikler — `#ozellikler` çapası, `.card-grid` içinde altı [FeatureCard]. */
@Composable
private fun FeaturesSection() {
    SectionContainer(id = "ozellikler") {
        H2(attrs = { classes("t-h2") }) { Text("Neden Atama Bildirim?") }
        Div(
            attrs = {
                classes("card-grid")
                attr("style", "margin-top:var(--s8)")
            },
        ) {
            FeatureCard(
                icon = "📋",
                title = "Tek yerde tüm ilanlar",
                body = "Bakanlık, belediye ve kamu kurumlarının güncel iş ilanları tek uygulamada toplanır.",
            )
            FeatureCard(
                icon = "🎯",
                title = "Sana uygun ilanlar",
                body = "Eğitim düzeyin, alanın ve tercihlerine göre sana en uygun ilanlar öne çıkar.",
            )
            FeatureCard(
                icon = "🔔",
                title = "Anlık bildirim",
                body = "Seçtiğin alanda yeni bir ilan açıldığında telefonuna anında bildirim gelir.",
            )
            FeatureCard(
                icon = "⚡",
                title = "Hızlı başvuru",
                body = "Başvuru başlangıç ve bitiş tarihlerini takip et, başvuru sayfasına tek dokunuşla ulaş.",
            )
            FeatureCard(
                icon = "🗂️",
                title = "Başvuru takibi",
                body = "Başvurduğun ilanları tek ekrandan izle, süreçlerini kolayca yönet.",
            )
            FeatureCard(
                icon = "📈",
                title = "Profil skoru",
                body = "Profilini tamamla, uygunluğunu artır ve sana özel önerileri gör.",
            )
        }
    }
}

/**
 * Nasıl çalışır — `#nasil-calisir` çapası, `.steps-grid` içinde dört adım kartı. Adım numarası
 * `.step::before { content: attr(data-step) }` CSS kuralı üzerinden gelir; bu yüzden her
 * `.step` elemanına `data-step` attribute'u basılır (ayrıca ikon/numara child eklenmez).
 */
@Composable
private fun HowItWorksSection() {
    SectionContainer(id = "nasil-calisir") {
        H2(attrs = { classes("t-h2") }) { Text("Nasıl çalışır?") }
        Div(
            attrs = {
                classes("steps-grid")
                attr("style", "margin-top:var(--s8)")
            },
        ) {
            Step(
                number = "1",
                title = "İndir ve profilini oluştur",
                body = "Uygulamayı ücretsiz indir, birkaç adımda profilini hazırla.",
            )
            Step(
                number = "2",
                title = "İlgilendiğin alanları seç",
                body = "Çalışmak istediğin kamu alanlarını belirle.",
            )
            Step(
                number = "3",
                title = "Bildirimini al",
                body = "Seçtiğin alanda yeni ilan açıldığında anında haberdar ol.",
            )
            Step(
                number = "4",
                title = "İncele ve başvur",
                body = "İlan detayını gör, uygunsan hızlıca başvur.",
            )
        }
    }
}

@Composable
private fun Step(number: String, title: String, body: String) {
    Div(
        attrs = {
            classes("step")
            attr("data-step", number)
        },
    ) {
        H3(attrs = { classes("t-title") }) { Text(title) }
        P(attrs = { classes("t-body") }) { Text(body) }
    }
}

/** Ekran görüntüleri — yatay kaydırılan `.shots-scroller` içinde 5 görsel. */
@Composable
private fun ScreenshotsSection() {
    SectionContainer {
        H2(attrs = { classes("t-h2") }) { Text("Uygulamadan görünümler") }
        Div(
            attrs = {
                classes("shots-scroller")
                attr("style", "margin-top:var(--s8)")
            },
        ) {
            Img(src = "/screenshots/screenshot_02.png", alt = "İlan listesi", attrs = { classes("shot") })
            Img(src = "/screenshots/screenshot_03.png", alt = "Başvuru takibi", attrs = { classes("shot") })
            Img(src = "/screenshots/screenshot_04.png", alt = "Alan seçimi", attrs = { classes("shot") })
            Img(src = "/screenshots/screenshot_05.png", alt = "Profil tamamlama", attrs = { classes("shot") })
            Img(src = "/screenshots/screenshot_06.png", alt = "Hızlı başvuru", attrs = { classes("shot") })
        }
    }
}

/** SSS — `.card-grid` içinde üç soru-cevap kartı. */
@Composable
private fun FaqSection() {
    SectionContainer {
        H2(attrs = { classes("t-h2") }) { Text("Sık sorulan sorular") }
        Div(
            attrs = {
                classes("card-grid")
                attr("style", "margin-top:var(--s8)")
            },
        ) {
            FaqCard(
                question = "Atama Bildirim ücretsiz mi?",
                answer = "Uygulamayı ücretsiz indirip kullanabilirsin. İsteğe bağlı premium abonelikle " +
                    "tüm ilanlara sınırsız erişirsin.",
            )
            FaqCard(
                question = "İlanlar nereden geliyor?",
                answer = "Kamu kurumlarının resmi ilanları düzenli olarak derlenir ve güncel tutulur.",
            )
            FaqCard(
                question = "Bildirimler nasıl çalışıyor?",
                answer = "Seçtiğin alanlarda yeni bir ilan yayınlandığında telefonuna anında bildirim " +
                    "gönderilir.",
            )
        }
    }
}

@Composable
private fun FaqCard(question: String, answer: String) {
    Div(attrs = { classes("card") }) {
        H3(attrs = { classes("t-title") }) { Text(question) }
        P(
            attrs = {
                classes("t-body")
                attr("style", "margin-top:var(--s2)")
            },
        ) { Text(answer) }
    }
}

/** İndirme çağrısı — `#indir` çapası, sıkı dikey boşluklu (`section-tight`) tek kart. */
@Composable
private fun DownloadCtaSection() {
    SectionContainer(id = "indir", extraClass = "section-tight") {
        Div(
            attrs = {
                classes("card")
                attr(
                    "style",
                    "max-width:640px;margin:0 auto;text-align:center;" +
                        "display:flex;flex-direction:column;align-items:center;gap:var(--s6)",
                )
            },
        ) {
            H2(attrs = { classes("t-h2") }) { Text("Hemen ücretsiz indir") }
            P(attrs = { classes("t-body") }) {
                Text("Atama Bildirim'i indir, sana uygun kamu ilanlarını kaçırma.")
            }
            StoreBadges()
        }
    }
}
